import {
    AccountLicense,
    ContactUsLinks,
} from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiText } from '@experiences/ui-common';
import {
    openWindow,
    useLocalizedLinks,
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens from '@uipath/apollo-core';
import clsx from 'clsx';
import React, {
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import {
    matchPath,
    useLocation,
} from 'react-router';

import * as RouteNames from '../../common/constants/RouteNames';
import { routePaths } from '../../common/constants/routePaths';
import useCheckLicense from '../../common/hooks/useCheckLicense';
import { useTrialText } from '../../common/hooks/useTrialText';
import detectiveRobot from '../../images/detectiveRobot.png';
import flyingRobot from '../../images/flyingRobot.png';
import { useTelemetryHelper } from '../../telemetry/TelemetryHelper';

const useStyles = makeStyles((theme) =>
    createStyles({
        mainContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '70%',
        },
        link: {
            cursor: 'pointer',
            color: theme.palette.semantic.colorForegroundLink,
            '&:hover': { textDecoration: 'underline' },
        },
        header: {
            fontFamily: Tokens.FontFamily.FontTitle,
            fontWeight: 600,
            fontSize: '20px',
            textAlign: 'center',
        },
        description: {
            fontFamily: Tokens.FontFamily.FontTitle,
            fontWeight: 400,
            fontSize: '14px',
            textAlign: 'center',
            marginTop: '16px',
        },
        spacer: { marginTop: '16px' },
        '@media all and (max-width: 560px)': {
            oldAdminSpacing: {
                position: 'static',
                marginTop: '60px',
            },
        },
    }),
);

const UpgradeForFeature: React.FC<{
    upgradeTitle: string;
    upgradeMessage: string;
    documentationLink?: string;
    level: AccountLicense.PRO | AccountLicense.ENTERPRISE; // TODO: Deprecate this prop when EnablePricingV2 feature flag rolled out
    telemetryTitle: string;
}> = ({
    upgradeTitle, upgradeMessage, documentationLink, level, telemetryTitle,
}) => {

    const classes = useStyles();
    const getRoute = useRouteResolver();
    const navigate = useNavigateWithParams();
    const getLocalizedLink = useLocalizedLinks();
    const { pathname } = useLocation();

    const { formatMessage: translate } = useIntl();
    const { logEvent } = useTelemetryHelper();
    const { callToAction: callToActionButtonText } = useTrialText();
    const { getNextUpgradeTier } = useCheckLicense();
    const {
        nextTier, shouldContactSales: contactSalesCTA,
    } = getNextUpgradeTier();

    const EnablePricingV2 = useFeatureFlagValue(Features.EnablePricingV2.name);

    const upgradeToEnterprise = useMemo(() => level === AccountLicense.ENTERPRISE, [ level ]);

    const shouldContactSales = useMemo(() => {
        if (EnablePricingV2) {
            return contactSalesCTA;
        }
        return upgradeToEnterprise;
    }, [ EnablePricingV2, contactSalesCTA, upgradeToEnterprise ]);

    const nextUpgradeTier = useMemo(() => {
        if (EnablePricingV2) {
            return nextTier;
        }
        return AccountLicense[level];
    }, [ EnablePricingV2, level, nextTier ]);

    useEffect(() => {
        // Call once
        logEvent(telemetryTitle, { UpgradePageLicense: nextUpgradeTier });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upgradeButton = useCallback(() => {
        logEvent(`${telemetryTitle}${shouldContactSales ? 'Contact' : 'Upgrade'}`, { UpgradePageLicense: nextUpgradeTier });

        if (shouldContactSales) {
            openWindow(getLocalizedLink(ContactUsLinks));
        } else if (matchPath({ path: routePaths.adminIdentitiesRobots }, pathname)) {
            // special case for robots page
            navigate(getRoute(RouteNames.BuyProPresets));
        } else {
            navigate(getRoute(RouteNames.LicensingUpgradeToEnterprise));
        }
    }, [ getLocalizedLink, getRoute, logEvent, navigate, nextUpgradeTier, pathname, shouldContactSales, telemetryTitle ]);

    return (
        <div className={classes.mainContainer}>
            <div
                className={classes.mainContainer}
                style={{ maxWidth: '500px' }}>
                <UiText
                    className={classes.header}
                    data-cy="upgrade-title">
                    {upgradeTitle}
                </UiText>
                <UiText
                    className={clsx(classes.description, classes.spacer)}
                    data-cy="upgrade-message">
                    {upgradeMessage}
                </UiText>
                <img
                    src={upgradeToEnterprise ? detectiveRobot : flyingRobot}
                    alt='UiPath robot'
                    className={classes.spacer}
                />
                <Button
                    onClick={upgradeButton}
                    className={classes.spacer}
                    variant='contained'
                    style={{ width: '166px' }}
                    data-cy='upgrade-button'>
                    {EnablePricingV2 ? callToActionButtonText : (translate({ id: level === AccountLicense.ENTERPRISE ? 'CLIENT_CONTACT_SALES' : 'CLIENT_UPGRADE' }))}
                </Button>
                {documentationLink && <Link
                    target='_blank'
                    href={documentationLink}
                    onClick={() => logEvent(`${telemetryTitle}Docs`)}
                    data-cy='upgrade-link'>
                    <UiText className={clsx(classes.link, classes.spacer)}>
                        {translate({ id: 'CLIENT_VIEW_DOCUMENTATION' })}
                    </UiText>
                </Link>}
            </div>
        </div>
    );
};

export default UpgradeForFeature;
